import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { authRoutes } from "src/constants/auth-routes";
import { useAuth } from "src/contexts/auth-provider";
import { useSiteAdminsProfile } from "src/features/site-management/hooks/api/admins/use-site-admins-profile";
import useStore from "./use-store";
import { useAdminsProfile } from "src/features/organization/hooks/api/admins/use-admins-profile";
import { useAvailableSecurityAdminsSites } from "src/features/security/hooks/api/use-available-security-admins-sites";

type ReturnType = { tenantContext: boolean; siteContext: boolean; securityContext: boolean };

export const useSetViewContext = (): ReturnType => {
  const { user } = useStore();
  const auth = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const [access, setAccess] = useState({
    tenantContext: false,
    siteContext: false,
    securityContext: false,
  });

  const [hasFetchedAccess, setHasFetchedAccess] = useState(false);

  const tenantAdminsProfile = useAdminsProfile(
    !!auth.session?.access_token && !authRoutes.includes(location.pathname),
    false,
  );

  useEffect(() => {
    if (tenantAdminsProfile.isSuccess) {
      setAccess(prev => ({
        ...prev,
        tenantContext: true,
      }));
    }

    if (tenantAdminsProfile.isError) {
      setAccess(prev => ({
        ...prev,
        tenantContext: false,
      }));
    }
  }, [tenantAdminsProfile.isSuccess, tenantAdminsProfile.isError]);

  const siteAdminsProfile = useSiteAdminsProfile(
    !!auth.session?.access_token && !authRoutes.includes(location.pathname),
    false,
  );

  useEffect(() => {
    if (siteAdminsProfile.isSuccess) {
      setAccess(prev => ({
        ...prev,
        siteContext: true,
      }));
    }

    if (siteAdminsProfile.isError) {
      setAccess(prev => ({
        ...prev,
        siteContext: false,
      }));
    }
  }, [siteAdminsProfile.isSuccess, siteAdminsProfile.isError]);

  const availableSecurityAdminsSites = useAvailableSecurityAdminsSites(
    !!auth.session?.access_token && !authRoutes.includes(location.pathname),
    false,
  );

  useEffect(() => {
    if (availableSecurityAdminsSites.isSuccess && availableSecurityAdminsSites.data?.length > 0) {
      setAccess(prev => ({
        ...prev,
        securityContext: true,
      }));
    }

    if (
      availableSecurityAdminsSites.isError ||
      (!availableSecurityAdminsSites.isFetching && availableSecurityAdminsSites.data?.length === 0)
    ) {
      setAccess(prev => ({
        ...prev,
        securityContext: false,
      }));
    }
  }, [
    availableSecurityAdminsSites.isSuccess,
    availableSecurityAdminsSites.isError,
    availableSecurityAdminsSites.isFetching,
    availableSecurityAdminsSites.data?.length,
  ]);

  useEffect(() => {
    if (
      (tenantAdminsProfile.isSuccess || tenantAdminsProfile.isError) &&
      (siteAdminsProfile.isSuccess || siteAdminsProfile.isError) &&
      (availableSecurityAdminsSites.isSuccess || availableSecurityAdminsSites.isError)
    ) {
      setHasFetchedAccess(true);
    }
  }, [
    siteAdminsProfile.isError,
    siteAdminsProfile.isSuccess,
    tenantAdminsProfile.isError,
    tenantAdminsProfile.isSuccess,
    availableSecurityAdminsSites.isSuccess,
    availableSecurityAdminsSites.isError,
  ]);

  useEffect(() => {
    if (hasFetchedAccess) {
      switch (true) {
        case access.siteContext && !access.tenantContext && user.viewContext !== "site":
          user.setViewContext("site");
          user.setIsLoadingViewContextAccess(false);

          navigate("/");

          break;

        case !access.siteContext && access.tenantContext && user.viewContext !== "tenant":
          user.setViewContext("tenant");
          user.setIsLoadingViewContextAccess(false);

          navigate("/");

          break;

        case !access.siteContext &&
          !access.tenantContext &&
          access.securityContext &&
          user.viewContext !== "security":
          user.setViewContext("security");
          user.setIsLoadingViewContextAccess(false);

          navigate("/");

          break;

        default:
          user.setIsLoadingViewContextAccess(false);
          break;
      }
    }
  }, [access.siteContext, access.tenantContext, hasFetchedAccess, user]);

  return access;
};
